// src/data/marketsClient.js
//
// PLAN-S1 — Client for /api/markets, /api/plan, and the location plan endpoints.
// Mirrors the pattern in leasesClient.js: typed fetch wrappers, camelCase
// domain models, recordAudit() for every mutating call.
//
// Endpoints covered:
//   getMarkets()                          GET  /api/markets
//   getMarket(id)                         GET  /api/markets/:id
//   getMarketActivity(id)                 GET  /api/markets/:id/activity (viewer+)
//   createMarket(body)                    POST /api/markets         (admin+)
//   updateMarket(id, patch)               PATCH /api/markets/:id    (editor+)
//   deleteMarket(id)                      DELETE /api/markets/:id   (admin+)
//   updateLocationPlan(locationId, patch) PATCH /api/locations/:id/plan (editor+) — legacy
//
// PLAN-S1 Direction C endpoints:
//   getPlan()                             GET  /api/plan                      (viewer+)
//   getPlanUsers()                        GET  /api/plan/users                (viewer+)
//   getPlanActivity(locationId)           GET  /api/plan/locations/:id/activity (viewer+)
//   updatePlanLocation(id, patch)         PUT  /api/plan/locations/:id        (editor+)
//   updatePlanMarket(id, patch)           PUT  /api/plan/markets/:id          (editor+)
//   uploadMarketImage(id, file)           POST /api/plan/markets/:id/image    (editor+)

import {
  AuthError,
  NetworkError,
  NotFoundError,
  ValidationError,
  errorFromResponse,
} from "./errors.js";
import { recordAudit } from "../services/auditLog.js";
import { log } from "../utils/logger.js";
import { T } from "../app/tokens.js";
import { getEffectiveSpacePolicy } from "../features/plan/policyClient.js";

// ─── Constants ────────────────────────────────────────────────────────────────

export const DISPOSITION_VALUES = [
  "stay", "expand", "consolidate", "relocate", "rightsize", "exit", "prospect",
];

export const DISPOSITION_META = {
  stay:        { label: "Stay / Renew",  color: T.marketStayFg,      bg: T.ragGreenBg         },
  expand:      { label: "Expand",        color: T.marketExpandFg,    bg: T.marketExpandBg     },
  consolidate: { label: "Consolidate",   color: T.purpleDeep,        bg: T.marketConsolidateBg},
  relocate:    { label: "Relocate",      color: T.dispRelocateFg,    bg: T.dispRelocateBg     },
  rightsize:   { label: "Right-size",    color: T.marketRightsizeFg, bg: T.marketRightsizeBg  },
  exit:        { label: "Exit",          color: T.marketExitFg,      bg: T.marketExitBg       },
  prospect:    { label: "Prospect",      color: T.marketProspectFg,  bg: T.bg                 },
};

export const CLARITY_META = {
  clear:   { label: "Clear",   color: T.marketStayFg      },
  forming: { label: "Forming", color: T.marketRightsizeFg },
  unclear: { label: "Unclear", color: T.marketExitFg      },
};

// Direction C plan disposition vocabulary — 6-value set after DISPOSITION-CONSOLIDATE.
// consolidate/relocate/vacate are folded into 'exit' with an exit_reason sub-field.
export const PLAN_DISPOSITION_VALUES = [
  "stay", "expand", "rightsize", "exit", "prospect", "undecided",
];

// ─── Internal fetch wrapper ──────────────────────────────────────────────────

async function jsonRequest(url, init) {
  let res;
  try {
    res = await fetch(url, {
      ...init,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    });
  } catch (err) {
    log.error("marketsClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    throw e;
  }
  if (res.status === 204) return null;
  return res.json();
}

// ─── Reads ───────────────────────────────────────────────────────────────────

/**
 * Fetch all markets with embedded location counts.
 * @returns {Promise<object[]>}
 */
export async function getMarkets() {
  log.ui("marketsClient: getMarkets");
  return jsonRequest("/api/markets");
}

/**
 * Fetch only active registry markets for entity-backed office creation.
 * Inactive and soft-deleted registry rows must never be selectable.
 */
export async function getActiveMarkets() {
  log.ui("marketsClient: getActiveMarkets");
  return jsonRequest("/api/markets?view=active");
}

/**
 * Fetch a single market with its locations embedded.
 * @param {string} marketId
 * @returns {Promise<object>}
 */
export async function getMarket(marketId) {
  log.ui("marketsClient: getMarket", { marketId });
  return jsonRequest(`/api/markets/${encodeURIComponent(marketId)}`);
}

/**
 * Fetch the full plan payload (Direction C).
 * Returns markets[] each with embedded locations[] (plan fields + derived lease facts).
 * In-flight deduplication: if a request is already in progress, returns the
 * same Promise rather than issuing a second network request.
 * @returns {Promise<object[]>}
 */
let _pendingPlan = null;
export function getPlan() {
  log.ui("marketsClient: getPlan");
  if (_pendingPlan) return _pendingPlan;
  _pendingPlan = jsonRequest("/api/plan").finally(() => { _pendingPlan = null; });
  return _pendingPlan;
}

/**
 * Fetch portfolio-level cost breakdown: lease rent vs workplace/security opex.
 * GET /api/locations/portfolio/cost-summary
 * Used by the Annual Cost KPI tile breakdown tooltip.
 * @returns {Promise<{leaseTotal: number|null, wpTotal: number|null, secTotal: number|null, coveredCount: number, totalCount: number}>}
 */
export async function getPortfolioCostSummary() {
  log.ui("marketsClient: getPortfolioCostSummary");
  return jsonRequest("/api/locations/portfolio/cost-summary");
}

/**
 * MARKET-PLAN-LEADERSHIP-VIEW-S1 — fetch prior-month portfolio KPI snapshot
 * for the Plan KPI strip's QoQ deltas.
 * @returns {Promise<{available: boolean, month: string|null, kpis: object|null}>}
 */
export async function getPlanKpiHistory() {
  log.ui("marketsClient: getPlanKpiHistory");
  return jsonRequest("/api/plan/kpi-history");
}

/**
 * Fetch the Market Pressure projection payload.
 * GET /api/plan/market-pressure?horizon=18&basis=usable
 *
 * @param {12|18|24}       [horizon=18]  Forward projection window in months.
 * @param {'usable'|'seats'} [basis='usable']  Capacity basis for RAG verdict.
 * @returns {Promise<{ markets: object[], quarters: object[], kpis: object }>}
 */
export async function getCapacity() {
  return jsonRequest("/api/plan/capacity");
}

export async function getMarketPressure(horizon = 18, basis = "usable", layer = "as-committed") {
  log.ui("marketsClient: getMarketPressure", { horizon, basis, layer });
  return jsonRequest(
    `/api/plan/market-pressure?horizon=${encodeURIComponent(horizon)}&basis=${encodeURIComponent(basis)}&layer=${encodeURIComponent(layer)}`
  );
}

/**
 * Fetch the list of users available for owner assignment in the plan drawer.
 * @returns {Promise<Array<{id: string, displayName: string, email: string}>>}
 */
export async function getPlanUsers() {
  log.ui("marketsClient: getPlanUsers");
  return jsonRequest("/api/plan/users");
}

/**
 * Fetch the audit_log activity for a specific location_plan.
 * @param {string} locationId
 * @returns {Promise<Array<{id, action, userId, before, after, createdAt}>>}
 */
export async function getPlanActivity(locationId) {
  log.ui("marketsClient: getPlanActivity", { locationId });
  return jsonRequest(`/api/plan/locations/${encodeURIComponent(locationId)}/activity`);
}

/**
 * SCENARIO-SEED-FROM-POLICY: Fetch the resolved office policy baseline
 * for What-If lever seeding and guardrail chips. Read-only (viewer+).
 * @returns {Promise<{
 *   rtoDaysPerMonth: number,
 *   sharingRatio: number,
 *   hotelingHoldPct: number,
 *   confGivebackPct: number,
 *   teamBlockWastePct: number,
 *   densityFloorSfPerSeat: number,
 *   targetBufferPct: number,
 * }>}
 */
export async function getPlanPolicy() {
  log.ui("marketsClient: getPlanPolicy");
  return getEffectiveSpacePolicy();
}

/**
 * Fetch the rename/region-change history for a market.
 * Surfaces market.update audit entries where name or region changed.
 * @param {string} marketId
 * @returns {Promise<Array<{id, userId, userDisplayName, before, after, createdAt}>>}
 */
export async function getMarketActivity(marketId) {
  log.ui("marketsClient: getMarketActivity", { marketId });
  return jsonRequest(`/api/markets/${encodeURIComponent(marketId)}/activity`);
}

// ─── Writes ──────────────────────────────────────────────────────────────────

/**
 * Create a new market. Admin only.
 * @param {object} body  { id, name, region, marketIntent?, planClarity?, owner?, summary?, links? }
 * @returns {Promise<object>}
 */
export async function createMarket(body) {
  log.ui("marketsClient: createMarket", { id: body?.id });
  const created = await jsonRequest("/api/markets", {
    method: "POST",
    body: JSON.stringify(body),
  });
  recordAudit({
    action:     "market.create",
    entityType: "market",
    entityId:   created.id,
    after:      created,
  });
  return created;
}

/**
 * Update mutable market fields via /api/markets (legacy endpoint). Editor+.
 * @param {string} marketId
 * @param {object} patch  { marketIntent?, planClarity?, owner?, summary?, links? }
 * @param {object} [before]  Pre-mutation snapshot for audit envelope.
 * @returns {Promise<object>}
 */
export async function updateMarket(marketId, patch, before = null) {
  log.ui("marketsClient: updateMarket", { marketId });
  const after = await jsonRequest(`/api/markets/${encodeURIComponent(marketId)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  recordAudit({
    action:     "market.update",
    entityType: "market",
    entityId:   marketId,
    before,
    after,
  });
  return after;
}

/**
 * Update plan-specific market fields (planNote, strategy, strategyTargetDate) via /api/plan. Editor+.
 * @param {string} marketId
 * @param {object} patch  { planNote?, strategy?, strategyTargetDate? }
 * @param {object} [before]  Pre-mutation snapshot for audit envelope.
 * @returns {Promise<object>}
 */
export async function updatePlanMarket(marketId, patch, before = null) {
  log.ui("marketsClient: updatePlanMarket", { marketId });
  const after = await jsonRequest(`/api/plan/markets/${encodeURIComponent(marketId)}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
  recordAudit({
    action:     "market.update",
    entityType: "market",
    entityId:   marketId,
    before,
    after,
  });
  return after;
}

/**
 * Delete a market. Admin only.
 * @param {string} marketId
 * @param {object} [before]  Pre-deletion snapshot for audit envelope.
 * @returns {Promise<null>}
 */
export async function deleteMarket(marketId, before = null) {
  log.ui("marketsClient: deleteMarket", { marketId });
  await jsonRequest(`/api/markets/${encodeURIComponent(marketId)}`, { method: "DELETE" });
  recordAudit({
    action:     "market.delete",
    entityType: "market",
    entityId:   marketId,
    before,
  });
  return null;
}

/**
 * Update a location's plan row via the Direction C endpoint. Editor+.
 * @param {string} locationId
 * @param {object} patch  { disposition?, targetActionDate?, planNote?, planHealth?, ownerUserId? }
 * @param {object} [before]  Pre-mutation snapshot for audit envelope.
 * @returns {Promise<object>}  Updated location_plans row
 */
export async function updatePlanLocation(locationId, patch, before = null) {
  log.ui("marketsClient: updatePlanLocation", { locationId });
  const after = await jsonRequest(
    `/api/plan/locations/${encodeURIComponent(locationId)}`,
    { method: "PUT", body: JSON.stringify(patch) }
  );
  recordAudit({
    action:     "location_plan.update",
    entityType: "location_plan",
    entityId:   locationId,
    before,
    after,
  });
  return after;
}

/**
 * Upload a market thumbnail image. Editor+.
 * @param {string} marketId
 * @param {File} file  The image file from <input type="file">
 * @returns {Promise<object>}  Updated market row with new image_url
 */
export async function uploadMarketImage(marketId, file) {
  log.ui("marketsClient: uploadMarketImage", { marketId });
  const formData = new FormData();
  formData.append("image", file);

  let res;
  try {
    res = await fetch(`/api/plan/markets/${encodeURIComponent(marketId)}/image`, {
      method: "POST",
      credentials: "include",
      body: formData,
      // No Content-Type header — browser sets it with boundary for multipart
    });
  } catch (err) {
    log.error("marketsClient: uploadMarketImage network failure", { marketId, err: String(err) });
    throw new NetworkError(`Failed to upload image for market ${marketId}`, err);
  }

  if (!res.ok) {
    const e = await errorFromResponse(res);
    throw e;
  }

  const after = await res.json();
  recordAudit({
    action:     "market.upload_image",
    entityType: "market",
    entityId:   marketId,
    after,
  });
  return after;
}

// ─── PLAN-V6-S2: Thesis writes ───────────────────────────────────────────────

/**
 * Update a market's thesis and/or direction. Editor+.
 * PATCH /api/markets/:marketId/thesis
 * @param {string} marketId
 * @param {object} patch  { thesis?: string, thesisOrigin?: string, direction?: string }
 * @returns {Promise<object>}  Updated market row
 */
export async function patchMarketThesis(marketId, patch) {
  log.ui("marketsClient: patchMarketThesis", { marketId });
  return jsonRequest(`/api/markets/${encodeURIComponent(marketId)}/thesis`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

/**
 * Request an AI draft thesis for a market. Editor+.
 * POST /api/markets/:marketId/thesis/draft
 * Nothing is persisted — returns { suggestion: string, subText: string }.
 * @param {string} marketId
 * @returns {Promise<{ suggestion: string, subText: string }>}
 */
export async function draftMarketThesis(marketId) {
  log.ui("marketsClient: draftMarketThesis", { marketId });
  return jsonRequest(`/api/markets/${encodeURIComponent(marketId)}/thesis/draft`, {
    method: "POST",
  });
}

// ─── PLAN-V6-S3: Timeline reads + plan_milestone writes ──────────────────────

/**
 * Fetch the unified market plan timeline. Viewer+.
 * GET /api/plan/markets/:marketId/timeline
 * @param {string} marketId
 * @returns {Promise<Array>}
 */
export async function getMarketTimeline(marketId) {
  log.ui("marketsClient: getMarketTimeline", { marketId });
  return jsonRequest(`/api/plan/markets/${encodeURIComponent(marketId)}/timeline`);
}

/**
 * Create a manual plan milestone. Editor+.
 * POST /api/plan/markets/:marketId/milestones
 * @param {string} marketId
 * @param {object} body  { title, date?, type?, owner?, notes? }
 * @returns {Promise<object>}
 */
export async function createPlanMilestone(marketId, body) {
  log.ui("marketsClient: createPlanMilestone", { marketId });
  return jsonRequest(`/api/plan/markets/${encodeURIComponent(marketId)}/milestones`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Update a manual plan milestone. Editor+.
 * PATCH /api/plan/markets/:marketId/milestones/:id
 * @param {string} marketId
 * @param {string} milestoneId
 * @param {object} patch  { title?, date?, type?, owner?, notes? }
 * @returns {Promise<object>}
 */
export async function updatePlanMilestone(marketId, milestoneId, patch) {
  log.ui("marketsClient: updatePlanMilestone", { marketId, milestoneId });
  return jsonRequest(
    `/api/plan/markets/${encodeURIComponent(marketId)}/milestones/${encodeURIComponent(milestoneId)}`,
    { method: "PATCH", body: JSON.stringify(patch) }
  );
}

/**
 * Delete a manual plan milestone. Editor+.
 * DELETE /api/plan/markets/:marketId/milestones/:id
 * @param {string} marketId
 * @param {string} milestoneId
 * @returns {Promise<null>}
 */
export async function deletePlanMilestone(marketId, milestoneId) {
  log.ui("marketsClient: deletePlanMilestone", { marketId, milestoneId });
  return jsonRequest(
    `/api/plan/markets/${encodeURIComponent(marketId)}/milestones/${encodeURIComponent(milestoneId)}`,
    { method: "DELETE" }
  );
}

/**
 * Reassign a location to a different market. Editor+.
 * Patches locations.market_id and syncs the denormalized market display name.
 * @param {string} locationId
 * @param {string} marketId  The target markets.id
 * @returns {Promise<object>}  Updated location row
 */
export async function reassignLocationMarket(locationId, marketId, before = null) {
  log.ui("marketsClient: reassignLocationMarket", { locationId, marketId });
  const after = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}`,
    { method: "PATCH", body: JSON.stringify({ marketId }) }
  );
  recordAudit({
    action:     "location.market_reassign",
    entityType: "location",
    entityId:   locationId,
    before,
    after,
  });
  return after;
}

// ─── PLAN-V6-S4: plan_actions CRUD ───────────────────────────────────────────

/**
 * Fetch plan_actions for a market. Viewer+.
 * GET /api/plan/markets/:marketId/actions
 * @param {string} marketId
 * @param {{ includeDismissed?: boolean }} [opts]
 * @returns {Promise<Array>}
 */
export async function getMarketActions(marketId, opts = {}) {
  log.ui("marketsClient: getMarketActions", { marketId });
  const qs = opts.includeDismissed ? "?includeDismissed=1" : "";
  return jsonRequest(`/api/plan/markets/${encodeURIComponent(marketId)}/actions${qs}`);
}

/**
 * Create a manual plan action. Editor+.
 * POST /api/plan/markets/:marketId/actions
 * @param {string} marketId
 * @param {object} body  { title, dueDate?, owner?, linkedSourceObject?, reason? }
 * @returns {Promise<object>}
 */
export async function createPlanAction(marketId, body) {
  log.ui("marketsClient: createPlanAction", { marketId });
  return jsonRequest(`/api/plan/markets/${encodeURIComponent(marketId)}/actions`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Accept a suggested plan action (suggested → active). Editor+.
 * PATCH /api/plan/markets/:marketId/actions/:id/accept
 * @param {string} marketId
 * @param {string} id  plan_actions UUID
 * @returns {Promise<object>}
 */
export async function acceptPlanAction(marketId, id) {
  log.ui("marketsClient: acceptPlanAction", { marketId, id });
  return jsonRequest(
    `/api/plan/markets/${encodeURIComponent(marketId)}/actions/${encodeURIComponent(id)}/accept`,
    { method: "PATCH" }
  );
}

/**
 * Dismiss a suggested or active plan action. Editor+.
 * PATCH /api/plan/markets/:marketId/actions/:id/dismiss
 * @param {string} marketId
 * @param {string} id  plan_actions UUID
 * @param {string} [reason]  Optional freetext reason
 * @returns {Promise<object>}
 */
export async function dismissPlanAction(marketId, id, reason) {
  log.ui("marketsClient: dismissPlanAction", { marketId, id });
  return jsonRequest(
    `/api/plan/markets/${encodeURIComponent(marketId)}/actions/${encodeURIComponent(id)}/dismiss`,
    { method: "PATCH", body: JSON.stringify(reason ? { reason } : {}) }
  );
}

// ─── PLAN-V6-S5: Plan Impact read ────────────────────────────────────────────

/**
 * Fetch the Plan Impact (current vs. planned metrics) for a market. Viewer+.
 * GET /api/plan/markets/:marketId/impact
 * @param {string} marketId
 * @returns {Promise<{ hasEffects: boolean, rows: Array<{metric, current, planned, delta, favorable}> }>}
 */
export async function getMarketImpact(marketId) {
  log.ui("marketsClient: getMarketImpact", { marketId });
  return jsonRequest(`/api/plan/markets/${encodeURIComponent(marketId)}/impact`);
}

// ─── PLAN-V6-S6: Decision Brief reads + writes ───────────────────────────────

/**
 * Fetch the Decision Brief for a market. Viewer+.
 * GET /api/plan/markets/:marketId/brief
 * Returns stored brief or a transient assembled brief if none exists yet.
 * Includes sourceHash (current) and storedSourceHash (at last refresh).
 * @param {string} marketId
 * @returns {Promise<object>}
 */
export async function getMarketBrief(marketId) {
  log.ui("marketsClient: getMarketBrief", { marketId });
  return jsonRequest(`/api/plan/markets/${encodeURIComponent(marketId)}/brief`);
}

/**
 * Trigger a brief refresh from source facts. Editor+.
 * POST /api/plan/markets/:marketId/brief/refresh
 * Returns 409 when brief_origin='human' and force is not true.
 * @param {string}  marketId
 * @param {boolean} [force=false]  Pass true to overwrite a human edit.
 * @returns {Promise<object>}  Updated brief
 */
export async function refreshMarketBrief(marketId, force = false) {
  log.ui("marketsClient: refreshMarketBrief", { marketId, force });
  return jsonRequest(`/api/plan/markets/${encodeURIComponent(marketId)}/brief/refresh`, {
    method: "POST",
    body: JSON.stringify({ force }),
  });
}

/**
 * Persist a manual edit to the Decision Brief. Editor+.
 * PATCH /api/plan/markets/:marketId/brief
 * @param {string} marketId
 * @param {object} patch  { situationText?, recommendationText?, whyNowText?,
 *                          driversText?, confidenceText?, briefOrigin? }
 * @returns {Promise<object>}  Updated brief
 */
export async function patchMarketBrief(marketId, patch) {
  log.ui("marketsClient: patchMarketBrief", { marketId });
  return jsonRequest(`/api/plan/markets/${encodeURIComponent(marketId)}/brief`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

// ─── MARKETS-S5: candidates CRUD ─────────────────────────────────────────────

/**
 * List candidates for a market. Viewer+.
 * GET /api/plan/markets/:marketId/candidates
 * @param {string} marketId
 * @returns {Promise<Array>}
 */
export async function getCandidates(marketId) {
  log.ui("marketsClient: getCandidates", { marketId });
  return jsonRequest(`/api/plan/markets/${encodeURIComponent(marketId)}/candidates`);
}

/**
 * Create a candidate for a market. Editor+.
 * POST /api/plan/markets/:marketId/candidates
 * @param {string} marketId
 * @param {object} body  { name, status, address?, floor?, visitedDate?, leaseType?, broker?, pros?, cons? }
 * @returns {Promise<object>}  Created candidate row
 */
export async function createCandidate(marketId, body) {
  log.ui("marketsClient: createCandidate", { marketId });
  return jsonRequest(`/api/plan/markets/${encodeURIComponent(marketId)}/candidates`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Update a candidate. Editor+.
 * PUT /api/plan/markets/:marketId/candidates/:candidateId
 * @param {string} marketId
 * @param {string} candidateId
 * @param {object} body  Partial or full candidate fields
 * @returns {Promise<object>}  Updated candidate row
 */
export async function updateCandidate(marketId, candidateId, body) {
  log.ui("marketsClient: updateCandidate", { marketId, candidateId });
  return jsonRequest(
    `/api/plan/markets/${encodeURIComponent(marketId)}/candidates/${encodeURIComponent(candidateId)}`,
    { method: "PUT", body: JSON.stringify(body) }
  );
}

/**
 * Hard-delete a candidate. Editor+.
 * DELETE /api/plan/markets/:marketId/candidates/:candidateId
 * @param {string} marketId
 * @param {string} candidateId
 * @returns {Promise<void>}
 */
export async function deleteCandidate(marketId, candidateId) {
  log.ui("marketsClient: deleteCandidate", { marketId, candidateId });
  await jsonRequest(
    `/api/plan/markets/${encodeURIComponent(marketId)}/candidates/${encodeURIComponent(candidateId)}`,
    { method: "DELETE" }
  );
}

/**
 * Update a location's plan fields (disposition, dispositionTarget, dispositionNote).
 * Editor+. Legacy endpoint — targets PATCH /api/locations/:id/plan.
 * @param {string} locationId
 * @param {object} patch  { disposition?, dispositionTarget?, dispositionNote? }
 * @param {object} [before]  Pre-mutation snapshot for audit envelope.
 * @returns {Promise<object>}  Updated location row
 */
export async function updateLocationPlan(locationId, patch, before = null) {
  log.ui("marketsClient: updateLocationPlan", { locationId });
  const after = await jsonRequest(
    `/api/locations/${encodeURIComponent(locationId)}/plan`,
    { method: "PATCH", body: JSON.stringify(patch) }
  );
  recordAudit({
    action:     "location.plan.update",
    entityType: "location",
    entityId:   locationId,
    before,
    after,
  });
  return after;
}

/**
 * Activate or deactivate a market. Admin+.
 * PATCH /api/markets/:marketId/active
 * @param {string}  marketId
 * @param {boolean} active  true = reactivate, false = deactivate
 * @returns {Promise<object>}  Updated market row
 */
export async function setMarketActive(marketId, active) {
  log.ui("marketsClient: setMarketActive", { marketId, active });
  return jsonRequest(
    `/api/markets/${encodeURIComponent(marketId)}/active`,
    { method: "PATCH", body: JSON.stringify({ active }) }
  );
}
